import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ArrowUp, ArrowDown, Minus } from 'lucide-react';
import DashboardCard from './DashboardCard';
import { budgetBand, formatTry } from '../constants';
import { Footnote } from '../styles';

const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Tile = styled.div`
  border: 1px solid #E5EAF0;
  border-radius: 6px;
  padding: 10px 12px;
  text-align: center;
`;

const LocName = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LocAmount = styled.div`
  font-size: 16px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 4px 0 2px;
`;

const SubLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 700;
  color: ${({ $color }) => $color || '#6B7280'};
`;

export default function LocationDistributionCard({ slice }) {
  const { t } = useTranslation();
  const data = slice.data;
  const items = data?.items || [];

  return (
    <DashboardCard
      cardKey="sube-dagilimi"
      title={t('expenseDashboard.locations.title')}
      meta={t('expenseDashboard.locations.meta')}
      span={8}
      spanMd={6}
      loading={slice.loading}
      empty={items.length === 0}
      emptyText={t('expenseDashboard.locations.empty')}
    >
      <div>
        <Tiles>
          {items.map((item) => {
            const delta = item.prevMonthTotal > 0
              ? Math.round(((item.monthTotal - item.prevMonthTotal) / item.prevMonthTotal) * 100)
              : null;
            const DeltaIcon = delta === null || delta === 0 ? Minus : delta > 0 ? ArrowUp : ArrowDown;
            return (
              <Tile key={item.locationId || 'none'}>
                <LocName title={item.location || t('expenseDashboard.locations.unassigned')}>
                  {item.location || t('expenseDashboard.locations.unassigned')}
                </LocName>
                <LocAmount>{formatTry(item.monthTotal)}</LocAmount>
                <SubLine>
                  <DeltaIcon size={11} />
                  {delta === null ? '—' : `%${Math.abs(delta)}`}
                </SubLine>
                {item.budgetUsagePct != null && (
                  <SubLine $color={budgetBand(item.budgetUsagePct)}>
                    {t('expenseDashboard.locations.budgetPct', { pct: item.budgetUsagePct })}
                  </SubLine>
                )}
              </Tile>
            );
          })}
        </Tiles>
        {data?.nonTryCount > 0 && <Footnote>{t('expenseDashboard.fxExcluded', { count: data.nonTryCount })}</Footnote>}
      </div>
    </DashboardCard>
  );
}
